import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import BugReportRoundedIcon from '@mui/icons-material/BugReportRounded';
import CasinoOutlinedIcon from '@mui/icons-material/CasinoOutlined';
import CasinoRoundedIcon from '@mui/icons-material/CasinoRounded';
import ContactPageOutlinedIcon from '@mui/icons-material/ContactPageOutlined';
import ContactPageRoundedIcon from '@mui/icons-material/ContactPageRounded';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import EmojiEmotionsRoundedIcon from '@mui/icons-material/EmojiEmotionsRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import Filter1RoundedIcon from '@mui/icons-material/Filter1Rounded';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import LocalPoliceOutlinedIcon from '@mui/icons-material/LocalPoliceOutlined';
import LocalPoliceRoundedIcon from '@mui/icons-material/LocalPoliceRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import { useCallback, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { shallowEqual } from 'react-redux';
import { IconWithUpdates } from '../components/iconWithUpdates';
import { useSocketSend } from '../context/webSocket/webSocketContext';
import { useIsRowLayoutMode } from '../hooks/useIsRowLayoutMode';
import { BombIcon } from '../icons/bombIcon';
import { ChatIcon } from '../icons/chatIcon';
import { ChatOutlinedIcon } from '../icons/chatOutlinedIcon';
import { InfoIcon } from '../icons/infoIcon';
import { InfoOutlinedIcon } from '../icons/infoOutlinedIcon';
import { PlayersIcon } from '../icons/playersIcon';
import { thunkSetActivePanel } from '../redux/actions/panelsActions';
import { selectIsTourneyRoom } from '../redux/selectorFns';
import { useAppDispatch, useAppSelector } from '../redux/store';
import { HEADER_BACKGROUND_COLOR, RIGHT_PANEL_ACTIVE_BUTTON_BACKGROUND_COLOR, RIGHT_PANEL_BUTTON_HOVER_COLOR, RIGHT_PANEL_TOURNEY_ROOM_ACTIVE_BUTTON_BACKGROUND_COLOR, RIGHT_PANEL_TOURNEY_ROOM_BUTTON_HOVER_COLOR, TOURNEY_HEADER_BACKGROUND_COLOR, YELLOW_COLOR } from '../styles/colors';
import { ALL_PANEL_PROPERTIES } from '../types/panelTypes';
const ACTIVE_ICON_SCALE = 115;
export const ALL_PANEL_BUTTON_PROPERTIES = {
    chat: {
        label: 'Chat',
        shownIcon: ChatIcon,
        hiddenIcon: ChatOutlinedIcon,
        clearUpdatesWhenActivated: true
    },
    friends: {
        label: 'Friends',
        shownIcon: FavoriteRoundedIcon,
        hiddenIcon: FavoriteBorderRoundedIcon
    },
    playerInfo: {
        label: 'Player Info',
        shownIcon: ContactPageRoundedIcon,
        hiddenIcon: ContactPageOutlinedIcon
    },
    pokerRoomInfo: {
        label: 'Room Info',
        shownIcon: InfoIcon,
        hiddenIcon: InfoOutlinedIcon
    },
    directMessages: {
        label: 'Direct Messages',
        shownIcon: EmailRoundedIcon,
        hiddenIcon: EmailOutlinedIcon
    },
    players: {
        label: 'Players',
        shownIcon: PlayersIcon,
        hiddenIcon: PlayersIcon
    },
    self: {
        label: 'Self',
        shownIcon: EmojiEmotionsRoundedIcon,
        hiddenIcon: EmojiEmotionsOutlinedIcon
    },
    tourneyStandings: {
        label: 'Tourney Standings',
        shownIcon: Filter1RoundedIcon,
        hiddenIcon: Filter1RoundedIcon
    },
    bombs: {
        label: 'Bombs & Charms',
        shownIcon: BombIcon,
        hiddenIcon: BombIcon
    },
    history: {
        label: 'Hand History',
        shownIcon: MenuBookRoundedIcon,
        hiddenIcon: MenuBookRoundedIcon
    },
    debug: {
        label: 'Debug Info',
        shownIcon: BugReportRoundedIcon,
        hiddenIcon: BugReportOutlinedIcon
    },
    sideBets: {
        label: 'Side Bets',
        shownIcon: CasinoRoundedIcon,
        hiddenIcon: CasinoOutlinedIcon
    },
    admin: {
        label: 'Admin Panel',
        shownIcon: LocalPoliceRoundedIcon,
        hiddenIcon: LocalPoliceOutlinedIcon
    }
};
const INACTIVE_ICON_COLOR = '#999999';
// On mobile, the hide icon is always visible, so use a subtler color that doesn't attract too much attention:
const HIDE_ICON_COLOR = isMobile ? INACTIVE_ICON_COLOR : YELLOW_COLOR;
export const BASE_PANEL_BUTTON_CSS = css `
  position: relative;
  padding: 1px 5px;
  cursor: pointer;
  background-color: ${HEADER_BACKGROUND_COLOR};
  border: none;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  margin-top: 1px;
`;
export const BASE_PANEL_TOURNEY_ROOM_BUTTON_CSS = css(BASE_PANEL_BUTTON_CSS, css `
    background-color: ${TOURNEY_HEADER_BACKGROUND_COLOR};
  `);
const HIDE_ICON_CSS = css `
  position: absolute;
  top: -1px;
  left: 50%;
  transform: translateX(-50%);
  color: ${HIDE_ICON_COLOR};
  filter: drop-shadow(1px 1px 0 black);
  pointer-events: none;
`;
const CLOSE_OVERLAY_ICON_CSS = css `
  transform: translateY(8px);
`;
function getActiveButtonBackgroundColor(panelType, isRowLayoutMode, isTourneyRoom) {
    if (!isRowLayoutMode) {
        return ALL_PANEL_PROPERTIES[panelType].backgroundColor;
    }
    return isTourneyRoom
        ? RIGHT_PANEL_TOURNEY_ROOM_ACTIVE_BUTTON_BACKGROUND_COLOR
        : RIGHT_PANEL_ACTIVE_BUTTON_BACKGROUND_COLOR;
}
export const RightPanelButton = ({ panelType, canHidePanel }) => {
    const isTourneyRoom = useAppSelector(selectIsTourneyRoom);
    const panelState = useAppSelector((state) => state.panels.panels.find(({ type }) => type === panelType), shallowEqual);
    const isActivePanel = useAppSelector((state) => state.panels.activePanelType === panelType);
    const isRowLayoutMode = useIsRowLayoutMode();
    const dispatch = useAppDispatch();
    const socketSend = useSocketSend();
    const [isHovering, setIsHovering] = useState(false);
    const onPointerDown = useCallback(() => {
        const newPanelType = isActivePanel && canHidePanel ? undefined : panelType;
        dispatch(thunkSetActivePanel(newPanelType, socketSend, true));
    }, [canHidePanel, dispatch, isActivePanel, panelType, socketSend]);
    const onMouseEnter = useCallback(() => {
        if (isMobile) {
            return;
        }
        setIsHovering(true);
    }, []);
    const onMouseLeave = useCallback(() => {
        if (isMobile) {
            return;
        }
        setIsHovering(false);
    }, []);
    const buttonCss = useMemo(() => {
        if (isActivePanel) {
            return css(isTourneyRoom ? BASE_PANEL_TOURNEY_ROOM_BUTTON_CSS : BASE_PANEL_BUTTON_CSS, css `
          color: white;
          background-color: ${getActiveButtonBackgroundColor(panelType, isRowLayoutMode, isTourneyRoom)};
          [data-testid='icon-scale-wrapper'] {
            transform: scale(${ACTIVE_ICON_SCALE}%);
          }
        `);
        }
        else {
            // inactive panel
            return css(isTourneyRoom ? BASE_PANEL_TOURNEY_ROOM_BUTTON_CSS : BASE_PANEL_BUTTON_CSS, css `
          color: ${INACTIVE_ICON_COLOR};
        `, !isMobile &&
                css `
            &:hover {
              background-color: ${isTourneyRoom
                    ? RIGHT_PANEL_TOURNEY_ROOM_BUTTON_HOVER_COLOR
                    : RIGHT_PANEL_BUTTON_HOVER_COLOR};
              [data-testid='icon-scale-wrapper'] {
                transform: scale(${ACTIVE_ICON_SCALE}%);
              }
            }
          `);
        }
    }, [isActivePanel, isRowLayoutMode, isTourneyRoom, panelType]);
    const showCloseOverlayIcon = (isHovering || isMobile) && isActivePanel && canHidePanel;
    const iconContainerCss = useMemo(() => css(showCloseOverlayIcon && CLOSE_OVERLAY_ICON_CSS, isActivePanel &&
        css `
            filter: drop-shadow(2px 2px 0 ${isTourneyRoom ? TOURNEY_HEADER_BACKGROUND_COLOR : HEADER_BACKGROUND_COLOR});
          `), [isActivePanel, isTourneyRoom, showCloseOverlayIcon]);
    if (!panelState) {
        return null;
    }
    const { numUpdates } = panelState;
    const { label, shownIcon: SvgShownIcon, hiddenIcon: SvgHiddenIcon } = ALL_PANEL_BUTTON_PROPERTIES[panelType];
    const buttonVerb = isActivePanel && canHidePanel ? 'Hide' : '';
    const title = buttonVerb ? `${buttonVerb} ${label}` : label;
    const icon = isActivePanel ? _jsx(SvgShownIcon, {}) : _jsx(SvgHiddenIcon, {});
    const additionalProps = isActivePanel ? { 'data-is-active': true } : undefined;
    return (_jsxs("button", { css: buttonCss, title: title, onPointerDown: onPointerDown, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, "aria-label": label, "data-testid": 'panel button', ...additionalProps, children: [showCloseOverlayIcon && (_jsx("div", { css: HIDE_ICON_CSS, children: _jsx(HighlightOffRoundedIcon, {}) })), _jsx("div", { css: iconContainerCss, children: _jsx("div", { "data-testid": 'icon-scale-wrapper', children: !isActivePanel && numUpdates ? _jsx(IconWithUpdates, { numUpdates: numUpdates, children: icon }) : icon }) })] }));
};
